"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasPromptToolStructuredOutputConflict = exports.PROMPT_TOOL_STRUCTURED_OUTPUT_CONFLICT_MESSAGE = void 0;
exports.parsePromptToolConfig = parsePromptToolConfig;
const zod_1 = __importDefault(require("zod"));
const types_1 = require("./types");
const EMPTY_TOOL_PARAMETERS = { type: "object", properties: {} };
const PromptToolDefinitionSchema = types_1.LLMToolDefinitionSchema.extend({
    name: types_1.LLMToolNameSchema,
    // OpenAI-compatible prompt configs may omit or explicitly null these fields.
    // Keep the runtime representation complete for createLLMToolSet.
    description: zod_1.default
        .string()
        .nullish()
        .transform((value) => value ?? ""),
    parameters: types_1.LLMJSONSchema.nullish().transform((value) => value ?? EMPTY_TOOL_PARAMETERS),
});
const PromptConfigToolSchema = zod_1.default.union([
    PromptToolDefinitionSchema,
    types_1.OpenAIToolSchema.extend({
        function: PromptToolDefinitionSchema,
    }).transform((tool) => tool.function),
]);
exports.PROMPT_TOOL_STRUCTURED_OUTPUT_CONFLICT_MESSAGE = "Your prompt contains tool definitions - tool calls are not compatible with structured output";
const hasPromptToolStructuredOutputConflict = (toolConfig, structuredOutputEnabled) => toolConfig.status === "valid" && structuredOutputEnabled;
exports.hasPromptToolStructuredOutputConflict = hasPromptToolStructuredOutputConflict;
/**
 * Extracts LLM tool definitions from a prompt's free-form `config` JSON.
 *
 * Accepts both the flat Langfuse shape (`{name, description, parameters}`)
 * and the OpenAI wrapper (`{type: "function", function: {...}}`).
 *
 * - `none`: config carries no tools — nothing to pass, nothing to warn about.
 * - `valid`: every entry parsed; pass `tools` to the LLM call.
 * - `invalid`: a `tools` key exists but cannot be used as a whole (not an
 *   array, an entry is malformed, or duplicate tool names). Callers must
 *   ignore ALL tools and surface a warning — running with a silently
 *   altered tool set would be worse than running without tools.
 */
function parsePromptToolConfig(config) {
    if (typeof config !== "object" || config === null || Array.isArray(config)) {
        return { status: "none" };
    }
    if (!("tools" in config)) {
        return { status: "none" };
    }
    const rawTools = config.tools;
    if (rawTools === null || rawTools === undefined) {
        return { status: "none" };
    }
    if (!Array.isArray(rawTools)) {
        return { status: "invalid" };
    }
    if (rawTools.length === 0) {
        return { status: "none" };
    }
    const parsed = zod_1.default.array(PromptConfigToolSchema).safeParse(rawTools);
    if (!parsed.success) {
        return { status: "invalid" };
    }
    const uniqueNames = new Set(parsed.data.map((tool) => tool.name));
    if (uniqueNames.size !== parsed.data.length) {
        return { status: "invalid" };
    }
    return { status: "valid", tools: parsed.data };
}
